import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdverts } from "@/hooks/content";
import { useTranslatedList } from "@/hooks/useTranslatedList";
import Reveal from "@/components/base/Reveal";

export default function AdvertsPage() {
  const { t } = useTranslation();
  const { items: adverts } = useAdverts();

  useEffect(() => {
    document.title = "Announcements & Advertisements | L+TV TÉLÉVISION";
  }, []);

  const active = useTranslatedList(
    adverts
      .filter((a) => a.status === "active")
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
    ["title", "tagline", "description", "placement"],
  );

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-primary-900 py-10">
        <div className="ltv-container">
          <Reveal direction="up">
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {t("adverts.title")}
            </h1>
            <p className="text-white/60 text-sm mt-2 max-w-2xl">
              {t("adverts.subtitle")}
            </p>
          </Reveal>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {active.length > 0 ? (
          <div className="space-y-8">
            {active.map((advert, index) => (
              <Reveal key={advert.id} direction="up" delay={index * 80}>
                <article className="bg-white rounded-lg border border-background-200 overflow-hidden">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative aspect-[16/10] md:aspect-auto overflow-hidden">
                      <img
                        src={advert.image}
                        alt={advert.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded inline-flex items-center gap-1">
                          <i className="ri-megaphone-line"></i>
                          {t("adverts.active")}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3 text-xs text-foreground-500">
                        <span>{advert.placement}</span>
                        <span>·</span>
                        <span>
                          {new Date(advert.startDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                        {advert.endDate && (
                          <>
                            <span>—</span>
                            <span>
                              {new Date(advert.endDate).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                          </>
                        )}
                      </div>
                      <h2 className="text-xl md:text-2xl font-bold text-foreground-900 leading-snug mb-2">
                        {advert.title}
                      </h2>
                      {advert.tagline && (
                        <p className="text-accent-600 font-medium text-sm md:text-base mb-3">
                          {advert.tagline}
                        </p>
                      )}
                      <p className="text-foreground-600 text-sm leading-relaxed mb-5 line-clamp-5">
                        {advert.description}
                      </p>
                      {advert.link && (
                        <Link
                          to={advert.link}
                          className="ltv-btn-accent inline-flex items-center gap-2 self-start text-sm"
                        >
                          {t("adverts.learnMore")}
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <i className="ri-megaphone-line text-5xl text-foreground-300 mb-4"></i>
            <p className="text-foreground-500">{t("adverts.noAdverts")}</p>
          </div>
        )}
      </div>
    </div>
  );
}